import React, { useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import CONFIG from "../config";
import BRANDING from "../branding";
import SEO from "../components/common/SEO";

const ForgotEmail = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');

  const [formData, setFormData] = useState({ username: "", password: "" });
  const [message, setMessage] = useState("");
  const [recoveredEmail, setRecoveredEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const currentAppName = isArabic ? BRANDING.appName.ar : BRANDING.appName.en;
  const appInitials = currentAppName
    .trim()
    .split(/[\s-]+/)
    .map(word => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase() || 'F-S';

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setRecoveredEmail("");
    try {
      const res = await axios.post(`${CONFIG.API_URL}/api/auth/recover-email`, formData);
      setRecoveredEmail(res.data.email);
    } catch (error) {
      setMessage(`❌ ${error.response?.data?.message || t('auth.error_occurred', 'An error occurred')}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#fcfcfc]">
      <Helmet>
        <html lang={isArabic ? "ar" : "en"} dir={isArabic ? "rtl" : "ltr"} />
        <title>
          {isArabic
            ? `نسيت البريد الإلكتروني | ${BRANDING.appName.ar}`
            : `Recover Email | ${BRANDING.appName.en}`}
        </title>
      </Helmet>
      <SEO />

      <div
        className="w-full max-w-5xl overflow-hidden flex flex-col md:flex-row shadow-2xl"
        style={{ backgroundColor: 'var(--bg-header)', borderRadius: 'var(--card-radius)', border: '1px solid var(--border)' }}
      >
        {/* Left Side - Visual/Branding */}
        <div className="md:w-1/2 bg-gray-900 relative p-12 flex flex-col justify-between text-white overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-primary/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-primary/5 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl"></div>

          <div className="relative z-10">
            <Link to="/" className="text-2xl font-black tracking-tighter hover:text-brand-primary transition-colors flex items-center gap-2">
              <span className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center text-white text-lg">{appInitials}</span>
              {isArabic ? BRANDING.appName.ar : BRANDING.appName.en}
            </Link>
          </div>

          <div className="relative z-10 mb-12">
            <h1 className="text-4xl md:text-5xl font-black mb-6 leading-tight">
              {t('auth.recover_email', 'Recover Email')}
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed max-w-sm">
              {isArabic
                ? "أدخل اسم المستخدم (أو الاسم الكامل) وكلمة المرور الخاصة بك لاسترجاع بريدك الإلكتروني."
                : "Enter your full name and password to retrieve your registered email address."
              }
            </p>
          </div>

          <div className="relative z-10 flex items-center gap-4 text-sm text-gray-400">
            <span>
              {isArabic ? "أمانك هو أولويتنا" : "Your security is our priority"}
            </span>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="md:w-1/2 p-8 md:p-16 flex flex-col justify-center">
          <div className="max-w-md mx-auto w-full">
            <div className="mb-10 text-center md:text-left">
              <h2 className="text-3xl font-black mb-2" style={{ color: 'var(--text-header)' }}>
                {t('auth.forgot_email', 'Forgot Email?')}
              </h2>
              <p className="text-gray-400 font-medium">
                {t('auth.forgot_email_desc', "Verify your identity to retrieve it.")}
              </p>
            </div>

            {!recoveredEmail ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-bold uppercase tracking-widest px-1" style={{ color: 'var(--text-header)' }}>
                    {t('auth.username', 'Full Name')}
                  </label>
                  <div className="relative">
                    <i className={`fas fa-user absolute ${isArabic ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-gray-400`}></i>
                    <input
                      type="text"
                      name="username"
                      placeholder={t('auth.full_name_placeholder', 'Full Name')}
                      onChange={handleChange}
                      className={`w-full ${isArabic ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-4 outline-none transition-all font-medium`}
                      style={{ backgroundColor: 'var(--bg-body)', border: '1px solid var(--border)', borderRadius: 'var(--input-radius)' }}
                      value={formData.username}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold uppercase tracking-widest px-1" style={{ color: 'var(--text-header)' }}>
                    {t('auth.password', 'Password')}
                  </label>
                  <div className="relative">
                    <i className={`fas fa-lock absolute ${isArabic ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-gray-400`}></i>
                    <input
                      type="password"
                      name="password"
                      placeholder={t('auth.password', 'Password')}
                      onChange={handleChange}
                      className={`w-full ${isArabic ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-4 outline-none transition-all font-medium`}
                      style={{ backgroundColor: 'var(--bg-body)', border: '1px solid var(--border)', borderRadius: 'var(--input-radius)' }}
                      value={formData.password}
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gray-900 text-white py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-brand-primary hover:shadow-xl hover:shadow-brand-primary/30 active:scale-[0.98] transition-all disabled:bg-gray-400 disabled:cursor-not-allowed transform"
                >
                  {loading ? t('auth.recovering', 'Recovering...') : t('auth.recover_btn', 'Recover Email')}
                </button>
              </form>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                  <i className="fas fa-check"></i>
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-header)' }}>{t('auth.email_found', 'Email Found')}</h3>
                <p className="text-gray-500 mb-6">{t('auth.your_email_is', 'Your registered email is:')}</p>
                <div className="bg-gray-100 p-4 rounded-xl font-mono text-lg font-bold mb-6 text-gray-800">
                  {recoveredEmail}
                </div>
                <Link to="/login" className="bg-brand-primary text-white py-3 px-8 rounded-xl font-bold hover:opacity-90 transition-opacity">
                  {t('auth.proceed_to_login', 'Proceed to Login')}
                </Link>
              </div>
            )}

            {message && !recoveredEmail && (
              <div className={`mt-6 p-4 rounded-2xl text-center text-sm font-bold bg-red-50 text-red-700 border border-red-100`}>
                {message}
              </div>
            )}

            <div className="mt-10 text-center space-y-4">
              <p className="text-gray-500 font-medium">
                {t('auth.remembered_email', 'Remembered your email?')} {" "}
                <Link to="/login" className="font-black hover:text-brand-primary transition-colors border-b-2 hover:border-brand-primary" style={{ color: 'var(--text-header)', borderColor: 'var(--text-header)' }}>
                  {t('auth.login', 'Login')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotEmail;
